package com.tovtalk.ui.navigation

import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.automirrored.outlined.Send
import androidx.compose.material.icons.filled.AccountCircle
import androidx.compose.material.icons.filled.Home
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.outlined.AccountCircle
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.Home
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material.icons.outlined.Settings
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.navigation.NavController
import androidx.navigation.NavType
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.navigation.compose.rememberNavController
import androidx.navigation.navArgument
import com.tovtalk.ui.adaptive.LocalWindowSizeClass
import com.tovtalk.ui.adaptive.WindowSizeClass
import com.tovtalk.ui.adaptive.isMobile
import com.tovtalk.ui.adaptive.rememberWindowSizeClass
import com.tovtalk.ui.components.AdaptiveScaffold
import com.tovtalk.ui.components.NavigationItem
import com.tovtalk.presentation.viewmodel.auth.AuthSheetController
import com.tovtalk.presentation.viewmodel.onboarding.OnboardingViewModel
import com.tovtalk.ui.screen.onboarding.OnboardingFlow
import com.tovtalk.utils.deeplink.DeepLinkLogger
import com.tovtalk.utils.deeplink.DeepLinkRoute
import com.tovtalk.utils.logger.Logger
import com.tovtalk.ui.screen.chat.ChatDetailScreen
import com.tovtalk.ui.screen.chat.InboxScreen
import com.tovtalk.ui.screen.chat.ChatListScreen
import com.tovtalk.ui.screen.chat.ChatRequestsScreen
import com.tovtalk.ui.screen.chat.ChatSplitView
import com.tovtalk.ui.screen.home.HomeScreen
import com.tovtalk.ui.screen.profile.ProfileFeedScreen
import com.tovtalk.ui.screen.profile.ProfileScreen
import com.tovtalk.ui.screen.profile.ProfileUserListScreen
import com.tovtalk.ui.screen.profile.EditProfileScreen
import com.tovtalk.presentation.viewmodel.chat.ChatListViewModel
import com.tovtalk.presentation.viewmodel.notifications.NotificationViewModel
import com.tovtalk.presentation.state.UiState
import com.tovtalk.di.koinInject
import com.tovtalk.di.disposableKoinInject
import com.tovtalk.di.getKoin
import com.tovtalk.ui.screen.hashtag.HashtagScreen
import com.tovtalk.ui.screen.post.PostDetailScreen
import com.tovtalk.ui.screen.search.SearchScreen
import com.tovtalk.presentation.viewmodel.hashtag.HashtagViewModel
import com.tovtalk.domain.repository.HashtagRepository
import com.tovtalk.ui.screen.settings.AboutScreen
import com.tovtalk.ui.screen.settings.NotificationSettingsScreen
import com.tovtalk.ui.screen.settings.BlockedUsersScreen
import com.tovtalk.ui.screen.settings.MyFollowingScreen
import com.tovtalk.ui.screen.settings.AccountDeletionScreen
import com.tovtalk.ui.screen.settings.PrivacyScreen
import com.tovtalk.ui.screen.settings.SafetyScreen
import com.tovtalk.ui.screen.settings.StaticPageScreen
import com.tovtalk.ui.screen.settings.ContentUploadManagementScreen
import com.tovtalk.ui.screen.settings.SettingsScreen
import com.tovtalk.ui.screen.settings.TermsScreen
import com.tovtalk.ui.screen.settings.SessionsScreen
import com.tovtalk.domain.entity.Post
import com.tovtalk.ui.screen.verification.PhoneVerificationScreen
import com.tovtalk.ui.screen.splash.OfflineLaunchScreen
import com.tovtalk.ui.screen.splash.SplashScreen
import com.tovtalk.ui.platform.WebUrlSync
import com.tovtalk.ui.platform.setReelContainerVisible
import com.tovtalk.ui.platform.getBottomSafeAreaInsetDp
import com.tovtalk.ui.components.MainBottomBar
import com.tovtalk.data.local.TokenManager
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.utils.deeplink.RedirectAfterLogin
import com.tovtalk.domain.repository.PostRepository
import com.tovtalk.ui.screen.story.StoryViewerScreen

// ── Navigation helpers ──────────────────────────────────────────────

private val fullScreenRoutes = setOf(
    Routes.SPLASH, Routes.OFFLINE_LAUNCH, Routes.ONBOARDING, Routes.PHONE_VERIFICATION, Routes.STORIES
)

// Helper to extract base route for navigation highlighting
private fun String?.toBaseRoute(): String = when {
    this == null -> "home"
    startsWith("home") -> "home"
    startsWith("chat") || startsWith("inbox") -> "messages"
    startsWith("upload") -> "upload"
    startsWith("search") -> "search"
    startsWith("notification") -> "messages"
    startsWith("profile") -> "profile"
    startsWith("settings") || startsWith("blocked") || startsWith("privacy") || startsWith("terms") || startsWith("safety") || startsWith("about") || startsWith("sessions") || startsWith("static_page") -> "settings"
    else -> this
}

private fun NavController.navigateHome(tab: Int = 0, startPostRef: String = "") {
    navigate("home?tab=$tab&playUserId=-1&playPostId=-1&startPostRef=$startPostRef") {
        popUpTo(Routes.HOME) { inclusive = true }
        launchSingleTop = true
    }
}

private fun NavController.navigateToOnboarding() {
    navigate(Routes.ONBOARDING) {
        popUpTo(Routes.HOME) { inclusive = true }
        launchSingleTop = true
    }
}

// ── Shared bottom bar removed – MainBottomBar is now data-driven,
// built inline in TovTalkNavHost using the same NavigationItem lists. ──

/**
 * TovTalk Navigation Host - shared across Android, iOS, and Web.
 * Receives platform callbacks for Toast, locale, and profile cache.
 * For Material Motion enter/exit transitions, use enterTransition/exitTransition
 * on composable() when the navigation-compose API supports them (e.g. fadeIn/fadeOut).
 */
@Composable
fun TovTalkNavHost(
    pendingDeepLink: DeepLinkRoute? = null,
    onDeepLinkConsumed: () -> Unit = {},
    onShowError: (String) -> Unit = {},
    onLanguageApplied: (String) -> Unit = {},
    onPhotoUploaded: ((Int) -> Unit)? = null,
    onOpenUrl: (String) -> Unit = {},
    onReelsLoaded: ((List<Post>) -> Unit)? = null,
    onReelIndexChanged: ((Int, List<Post>) -> Unit)? = null,
    onPreloadNextVideoUrl: ((String?) -> Unit)? = null,
    onDiscoverFirstPage: ((List<Post>) -> Unit)? = null,
) {
    val windowSizeClass = rememberWindowSizeClass()

    CompositionLocalProvider(LocalWindowSizeClass provides windowSizeClass) {

    val navController = rememberNavController()
    WebUrlSync(navController)
    val authSheetController: AuthSheetController = koinInject()

    val authState by TokenManager.authStateFlow.collectAsState()
    val isGuestMode = authState.isGuestMode

    // Session-expiry / logout redirect — shared across Android, iOS and Web. When a valid
    // session is lost (e.g. ApiClient clears tokens after a refresh-token 401), route back
    // to onboarding instead of stranding the user on an authenticated screen with every
    // request 401-ing. Previously this was wired only on Web (window.location), so Android
    // and iOS got stuck. We act only on a true→false transition so the initial
    // not-logged-in boot (handled by Splash) doesn't trigger a redirect.
    val wasLoggedIn = remember { mutableStateOf(authState.isLoggedIn) }
    LaunchedEffect(authState.isLoggedIn) {
        val sessionLost = wasLoggedIn.value && !authState.isLoggedIn
        wasLoggedIn.value = authState.isLoggedIn
        if (sessionLost) {
            val route = navController.currentDestination?.route
            if (route != null && route !in fullScreenRoutes) {
                Logger.w("Session lost — redirecting to onboarding (route=$route)", "ApiClient")
                navController.navigateToOnboarding()
            }
        }
    }
    // Rail bottom section: always just Settings
    val bottomNavItems = remember {
        listOf(
            NavigationItem(route = "settings", label = "הגדרות", icon = Icons.Outlined.Settings),
        )
    }

    fun navigateToDeepLinkRoute(route: DeepLinkRoute, onConsumed: () -> Unit) {
        when (route) {
            is DeepLinkRoute.Search -> {
                Logger.i("[DeepLink] ניווט ליעד: Search (tab=1)", "DeepLink")
                navController.navigate("home?tab=1&playUserId=-1&playPostId=-1&startPostRef=") {
                    popUpTo(Routes.HOME) { inclusive = true }
                    launchSingleTop = true
                }
                onConsumed()
            }
            is DeepLinkRoute.User -> {
                Logger.i("[DeepLink] ניווט ליעד: profile/by/${route.username}", "DeepLink")
                DeepLinkLogger.setPending(route)
                navController.navigate("profile/by/${route.username}") {
                    popUpTo(Routes.HOME) { inclusive = false }
                    launchSingleTop = true
                }
                onConsumed()
            }
            is DeepLinkRoute.Post, is DeepLinkRoute.Video -> {
                val postRef = when (route) {
                    is DeepLinkRoute.Post -> route.postRef
                    is DeepLinkRoute.Video -> route.postRef
                    else -> ""
                }
                if (postRef.isNotEmpty()) {
                    Logger.i("[DeepLink] ניווט ליעד: home עם startPostRef=$postRef", "DeepLink")
                    DeepLinkLogger.setPending(route)
                    navController.navigate("home?tab=0&playUserId=-1&playPostId=-1&startPostRef=$postRef") {
                        popUpTo(Routes.HOME) { inclusive = true }
                        launchSingleTop = true
                    }
                    onConsumed()
                }
            }
            is DeepLinkRoute.Chat -> {
                if (route.userId > 0) {
                    Logger.i("[DeepLink] ניווט ליעד: chat/${route.userId}", "DeepLink")
                    DeepLinkLogger.setPending(route)
                    navController.navigate("chat/${route.userId}/") {
                        popUpTo(Routes.HOME) { inclusive = false }
                        launchSingleTop = true
                    }
                    onConsumed()
                }
            }
            DeepLinkRoute.Home, DeepLinkRoute.Unknown -> { }
            is DeepLinkRoute.Privacy -> {
                Logger.i("[DeepLink] ניווט ליעד: Privacy", "DeepLink")
                navController.navigate(Routes.PRIVACY) {
                    popUpTo(Routes.HOME) { inclusive = false }
                    launchSingleTop = true
                }
                onConsumed()
            }
            is DeepLinkRoute.Terms -> {
                Logger.i("[DeepLink] ניווט ליעד: Terms", "DeepLink")
                navController.navigate(Routes.TERMS) {
                    popUpTo(Routes.HOME) { inclusive = false }
                    launchSingleTop = true
                }
                onConsumed()
            }
            is DeepLinkRoute.About -> {
                Logger.i("[DeepLink] ניווט ליעד: About", "DeepLink")
                navController.navigate(Routes.ABOUT) {
                    popUpTo(Routes.HOME) { inclusive = false }
                    launchSingleTop = true
                }
                onConsumed()
            }
            is DeepLinkRoute.AccountDeletion -> {
                Logger.i("[DeepLink] ניווט ליעד: AccountDeletion", "DeepLink")
                navController.navigate(Routes.ACCOUNT_DELETION) {
                    popUpTo(Routes.HOME) { inclusive = false }
                    launchSingleTop = true
                }
                onConsumed()
            }
            is DeepLinkRoute.StaticPage -> {
                Logger.i("[DeepLink] ניווט ליעד: StaticPage(${route.slug})", "DeepLink")
                navController.navigate(Routes.staticPage(route.slug)) {
                    popUpTo(Routes.HOME) { inclusive = false }
                    launchSingleTop = true
                }
                onConsumed()
            }
        }
    }

    LaunchedEffect(pendingDeepLink) {
        val route = pendingDeepLink ?: return@LaunchedEffect
        when (route) {
            DeepLinkRoute.Home -> Logger.i("[DeepLink] יעד: Home – ללא ניווט נוסף", "DeepLink")
            DeepLinkRoute.Unknown -> { }
            else -> {
                val currentRoute = navController.currentBackStackEntry?.destination?.route
                if (currentRoute == Routes.SPLASH) {
                    // Let Splash handle after language + login
                    Logger.i("[DeepLink] יש יעד דיפ לינק – מחכה ל-Splash (שפה + התחברות) לפני ניווט", "DeepLink")
                } else {
                    // App already open (e.g. user tapped notification) – navigate now
                    navigateToDeepLinkRoute(route, onDeepLinkConsumed)
                }
            }
        }
    }

    val currentRoute = navController.currentBackStackEntryAsState().value?.destination?.route
    val railHomeViewModel: com.tovtalk.presentation.viewmodel.home.HomeViewModel = remember { getKoin().get() }

    // On web the reel container is a persistent DOM element that keeps playing audio unless
    // explicitly hidden. Whenever we leave the Home route (Settings, Profile, Chat, Post detail, etc.)
    // hide + pause it. Re-showing on Home is handled by HomeScreen's own tab LaunchedEffect.
    LaunchedEffect(currentRoute) {
        if (currentRoute.toBaseRoute() != "home") {
            setReelContainerVisible(false)
        }
    }

    // ── Badge counts (shared by bottom bar and rail) ────────────────
    val userRepository: UserRepository = remember { getKoin().get() }
    val cachedUser by userRepository.getCachedUser().collectAsState(initial = null)
    val canUpload = !isGuestMode && cachedUser != null && cachedUser?.isReadOnly != true

    val notificationViewModel: NotificationViewModel = remember { getKoin().get() }
    val notificationUnreadCount by notificationViewModel.unreadCount.collectAsState(initial = 0)

    val chatListViewModel: ChatListViewModel = remember { getKoin().get() }
    val chatThreadsState by chatListViewModel.chatThreads.collectAsState()
    val chatRequestsState by chatListViewModel.chatRequests.collectAsState()

    val totalUnreadCount = remember(notificationUnreadCount, chatThreadsState, chatRequestsState) {
        if (isGuestMode) 0
        else {
            val chatUnread = (chatThreadsState as? UiState.Success)?.data?.sumOf { it.unreadCount } ?: 0
            val chatRequests = (chatRequestsState as? UiState.Success)?.data?.size ?: 0
            notificationUnreadCount + chatUnread + chatRequests
        }
    }

    // ── Build final item lists (inject Upload when allowed, add badges) ──
    // Same items for both phone bottom bar and tablet rail
    val finalNavItems = remember(canUpload, totalUnreadCount) {
        buildList {
            add(NavigationItem(route = "home", label = "בית", icon = Icons.Outlined.Home, selectedIcon = Icons.Filled.Home))
            add(NavigationItem(route = "search", label = "חיפוש", icon = Icons.Outlined.Search, selectedIcon = Icons.Filled.Search))
            if (canUpload) {
                add(NavigationItem(route = "upload", label = "העלאה", icon = Icons.Outlined.Add, isCtaButton = true))
            }
            add(NavigationItem(route = "messages", label = "הודעות", icon = Icons.AutoMirrored.Outlined.Send, selectedIcon = Icons.AutoMirrored.Filled.Send, badgeCount = totalUnreadCount))
            add(NavigationItem(route = "profile", label = "פרופיל", icon = Icons.Outlined.AccountCircle, selectedIcon = Icons.Filled.AccountCircle))
        }
    }

    val handleNavItemClick: (NavigationItem) -> Unit = { item ->
        when (item.route) {
            "home" -> {
                if (currentRoute.toBaseRoute() == "home") railHomeViewModel.refreshFeed()
                else navController.navigateHome()
            }
            "messages" -> {
                if (isGuestMode) authSheetController.open()
                else navController.navigate(Routes.MESSAGES)
            }
            "upload" -> navController.navigateHome(tab = 2)
            "search" -> navController.navigate(Routes.SEARCH)
            "profile" -> {
                if (isGuestMode) authSheetController.open()
                else navController.navigate("profile/-1/-1")
            }
            "settings" -> navController.navigate(Routes.SETTINGS)
        }
    }

    val showNavigation = currentRoute !in fullScreenRoutes

    AdaptiveScaffold(
        items = finalNavItems,
        selectedRoute = currentRoute.toBaseRoute(),
        onItemClick = handleNavItemClick,
        bottomItems = bottomNavItems,
        navigationRailContent = {
            androidx.compose.animation.AnimatedVisibility(
                visible = showNavigation,
                enter = androidx.compose.animation.fadeIn(
                    androidx.compose.animation.core.tween(400, easing = androidx.compose.animation.core.FastOutSlowInEasing)
                ),
                exit = androidx.compose.animation.fadeOut(
                    androidx.compose.animation.core.tween(400, easing = androidx.compose.animation.core.FastOutSlowInEasing)
                )
            ) {
                com.tovtalk.ui.components.TovTalkNavigationRail(
                    items = finalNavItems,
                    selectedRoute = currentRoute.toBaseRoute(),
                    onItemClick = handleNavItemClick,
                    bottomItems = bottomNavItems,
                )
            }
        },
        bottomBar = {
            androidx.compose.animation.AnimatedVisibility(
                visible = showNavigation,
                enter = androidx.compose.animation.fadeIn(
                    androidx.compose.animation.core.tween(400, easing = androidx.compose.animation.core.FastOutSlowInEasing)
                ),
                exit = androidx.compose.animation.fadeOut(
                    androidx.compose.animation.core.tween(400, easing = androidx.compose.animation.core.FastOutSlowInEasing)
                )
            ) {
                MainBottomBar(
                    items = finalNavItems,
                    selectedRoute = currentRoute.toBaseRoute(),
                    onItemClick = handleNavItemClick,
                    bottomPadding = getBottomSafeAreaInsetDp(),
                )
            }
        }
    ) {

    NavHost(
        navController = navController,
        startDestination = Routes.SPLASH
    ) {
        composable(
            route = Routes.SPLASH,
            enterTransition = fadeEnter,
            exitTransition = fadeExit,
            popEnterTransition = fadeEnter,
            popExitTransition = fadeExit
        ) {
            SplashScreen(
                pendingDeepLink = pendingDeepLink,
                onOpenUrl = onOpenUrl,
                onNavigateToOfflineLaunch = {
                    navController.navigate(Routes.OFFLINE_LAUNCH) {
                        popUpTo(Routes.SPLASH) { inclusive = true }
                    }
                },
                onNavigateToKashrutSelection = {
                    navController.navigate(Routes.ONBOARDING) {
                        popUpTo(Routes.SPLASH) { inclusive = true }
                    }
                },
                onNavigateToLogin = {
                    if (pendingDeepLink is DeepLinkRoute.Search || pendingDeepLink is DeepLinkRoute.User ||
                        pendingDeepLink is DeepLinkRoute.Post || pendingDeepLink is DeepLinkRoute.Video ||
                        pendingDeepLink is DeepLinkRoute.Chat) {
                        RedirectAfterLogin.route = pendingDeepLink
                    }
                    navController.navigate(Routes.ONBOARDING) {
                        popUpTo(Routes.SPLASH) { inclusive = true }
                    }
                },
                onNavigateToHome = {
                    navController.navigate("home?tab=0&playUserId=-1&playPostId=-1&startPostRef=") {
                        popUpTo(Routes.SPLASH) { inclusive = true }
                    }
                },
                onNavigateToDeepLink = { route ->
                    navigateToDeepLinkRoute(route, onDeepLinkConsumed)
                }
            )
        }

        composable(
            route = Routes.OFFLINE_LAUNCH,
            enterTransition = fadeEnter,
            exitTransition = fadeExit,
            popEnterTransition = fadeEnter,
            popExitTransition = fadeExit
        ) {
            OfflineLaunchScreen(
                onRetry = {
                    navController.navigate(Routes.SPLASH) {
                        popUpTo(Routes.OFFLINE_LAUNCH) { inclusive = true }
                    }
                }
            )
        }

        composable(Routes.ONBOARDING) {
            val onboardingViewModel: OnboardingViewModel = disposableKoinInject()
            OnboardingFlow(
                viewModel = onboardingViewModel,
                onDone = {
                    navController.navigate("home?tab=0&playUserId=-1&playPostId=-1&startPostRef=") {
                        popUpTo(Routes.ONBOARDING) { inclusive = true }
                    }
                }
            )
        }

        composable(
            route = Routes.EDIT_PROFILE,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            EditProfileScreen(
                onNavigateBack = { navController.popBackStack() },
                onPhotoUploaded = onPhotoUploaded
            )
        }

        composable(
            route = Routes.SETTINGS,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            SettingsScreen(
                onNavigateBack = { navController.popBackStack() },
                onNavigateToNotificationSettings = {
                    navController.navigate(Routes.NOTIFICATION_SETTINGS)
                },
                onNavigateToBlockedUsers = { navController.navigate(Routes.BLOCKED_USERS) },
                onNavigateToSessions = { navController.navigate(Routes.SESSIONS) },
                onNavigateToPhoneVerification = { navController.navigate(Routes.PHONE_VERIFICATION) },
                onNavigateToAbout = { navController.navigate(Routes.ABOUT) },
                onNavigateToPrivacy = { navController.navigate(Routes.PRIVACY) },
                onNavigateToTerms = { navController.navigate(Routes.TERMS) },
                onNavigateToLogin = { authSheetController.open() },
                onLogout = { navController.navigateToOnboarding() },
                onLanguageApplied = onLanguageApplied,
                onNavigateToContentUploadManagement = { navController.navigate(Routes.CONTENT_UPLOAD_MANAGEMENT) },
            )
        }

        composable(
            route = Routes.SESSIONS,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            SessionsScreen(
                onNavigateBack = { navController.popBackStack() }
            )
        }

        composable(
            route = Routes.PHONE_VERIFICATION,
            enterTransition = fadeEnter,
            exitTransition = fadeExit,
            popEnterTransition = fadeEnter,
            popExitTransition = fadeExit
        ) {
            PhoneVerificationScreen(
                onNavigateBack = { navController.popBackStack() }
            )
        }

        composable(
            route = Routes.NOTIFICATION_SETTINGS,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            NotificationSettingsScreen(
                onNavigateBack = { navController.popBackStack() },
                onNavigateToLogin = { authSheetController.open() }
            )
        }

        composable(
            route = Routes.CONTENT_UPLOAD_MANAGEMENT,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            ContentUploadManagementScreen(
                onNavigateBack = { navController.popBackStack() }
            )
        }

        composable(
            route = Routes.ABOUT,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            AboutScreen(
                onNavigateBack = { navController.popBackStack() }
            )
        }

        composable(
            route = Routes.PRIVACY,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            PrivacyScreen(
                onNavigateBack = { navController.popBackStack() }
            )
        }

        composable(
            route = Routes.TERMS,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            TermsScreen(
                onNavigateBack = { navController.popBackStack() }
            )
        }

        composable(
            route = Routes.ACCOUNT_DELETION,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            AccountDeletionScreen(
                onNavigateBack = { navController.popBackStack() }
            )
        }

        composable(
            route = Routes.SAFETY,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            SafetyScreen(
                onNavigateBack = { navController.popBackStack() }
            )
        }

        composable(
            route = Routes.STATIC_PAGE,
            arguments = listOf(navArgument("slug") { type = NavType.StringType }),
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) { backStackEntry ->
            val slug = getNavArgString(backStackEntry, "slug").orEmpty()
            StaticPageScreen(
                slug = slug,
                onNavigateBack = { navController.popBackStack() }
            )
        }

        composable(
            route = Routes.BLOCKED_USERS,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            BlockedUsersScreen(
                onNavigateBack = { navController.popBackStack() }
            )
        }

        composable(
            route = Routes.MY_FOLLOWING,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            MyFollowingScreen(
                onNavigateBack = { navController.popBackStack() },
                onNavigateToProfile = { userId -> navController.navigate("profile/$userId/-1") }
            )
        }

        composable(
            route = Routes.HOME,
            arguments = listOf(
                navArgument("tab") {
                    type = NavType.IntType
                    defaultValue = 0
                },
                navArgument("playUserId") {
                    type = NavType.IntType
                    defaultValue = -1
                },
                navArgument("playPostId") {
                    type = NavType.IntType
                    defaultValue = -1
                },
                navArgument("startPostRef") {
                    type = NavType.StringType
                    defaultValue = ""
                }
            ),
            enterTransition = fadeEnter,
            exitTransition = fadeExit,
            popEnterTransition = fadeEnter,
            popExitTransition = fadeExit
        ) { backStackEntry ->
            val tab = getNavArgInt(backStackEntry, "tab") ?: 0
            val playUserId = getNavArgInt(backStackEntry, "playUserId") ?: -1
            val playPostId = getNavArgInt(backStackEntry, "playPostId") ?: -1
            val startPostRef = getNavArgString(backStackEntry, "startPostRef")?.takeIf { it.isNotEmpty() }
            HomeScreen(
                initialTab = tab,
                initialPlayUserId = if (playUserId >= 0) playUserId else null,
                initialPlayPostId = if (playPostId >= 0) playPostId else null,
                startPostRef = startPostRef,
                onShowError = onShowError,
                onVideoBack = { navController.popBackStack() },
                onNavigateToTab = { newTab ->
                    when (newTab) {
                        1 -> navController.navigate(Routes.SEARCH)
                        3 -> navController.navigate("profile/-1/-1")
                        else -> navController.navigate("home?tab=$newTab&playUserId=-1&playPostId=-1&startPostRef=")
                    }
                },
                onNavigateToProfile = { userId, fromPostId ->
                    setReelContainerVisible(false)
                    navController.navigate("profile/$userId/${fromPostId ?: -1}")
                },
                onNavigateToPost = { userId, postId ->
                    navController.navigate("home?tab=0&playUserId=$userId&playPostId=$postId&startPostRef=")
                },
                onNavigateToPostDetail = { postRef, post ->
                    post?.let { com.tovtalk.data.local.PostDetailCache.set(it) }
                    navController.navigate("post/$postRef")
                },
                onNavigateToChat = { receiverId, receiverName ->
                    navController.navigate("chat/$receiverId/$receiverName")
                },
                onNavigateToRequests = {
                    navController.navigate(Routes.CHAT_REQUESTS)
                },
                onNavigateToSettings = { navController.navigate(Routes.SETTINGS) },
                onNavigateToEditProfile = { navController.navigate(Routes.EDIT_PROFILE) },
                onNavigateToMessages = { navController.navigate(Routes.MESSAGES) },
                onLogout = { navController.navigateToOnboarding() },
                onNavigateToLogin = { authSheetController.open() },
                onReelsLoaded = onReelsLoaded,
                onReelIndexChanged = onReelIndexChanged,
                onPreloadNextVideoUrl = onPreloadNextVideoUrl,
                onDiscoverFirstPage = onDiscoverFirstPage,
                onNavigateToFollowers = { id -> navController.navigate("profile/$id/followers") },
                onNavigateToFollowing = { id -> navController.navigate("profile/$id/following") },
                onHashtagClick = { tag -> navController.navigate("hashtag/$tag") },
                onNavigateToStories = { userId -> navController.navigate(Routes.stories(userId)) }
            )
        }

        composable(
            route = Routes.SEARCH,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            setReelContainerVisible(false)
            SearchScreen(
                onNavigateToProfile = { userId ->
                    setReelContainerVisible(false)
                    navController.navigate("profile/$userId/-1")
                },
                onPostClick = { post ->
                    com.tovtalk.data.local.PostDetailCache.set(post)
                    navController.navigate("post/${post.ref}")
                }
            )
        }

        composable(
            route = Routes.MESSAGES,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            InboxScreen(
                onNavigateToProfile = { userId ->
                    setReelContainerVisible(false)
                    navController.navigate("profile/$userId/-1")
                },
                onNavigateToPost = { postRef, post ->
                    post?.let { com.tovtalk.data.local.PostDetailCache.set(it) }
                    navController.navigate("post/$postRef")
                },
                onNavigateToChat = { receiverId, receiverName ->
                    navController.navigate("chat/$receiverId/$receiverName")
                },
                onNavigateToChatRequests = { navController.navigate(Routes.CHAT_REQUESTS) },
                onNavigateToStories = { userId -> navController.navigate(Routes.stories(userId)) }
            )
        }

        composable(
            route = Routes.PRIVATE_CHATS,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            if (isMobile()) {
                ChatListScreen(
                    onNavigateToChat = { receiverId, receiverName ->
                        navController.navigate("chat/$receiverId/$receiverName")
                    },
                    onNavigateBack = { navController.popBackStack() },
                    onNavigateToStories = { userId -> navController.navigate(Routes.stories(userId)) }
                )
            } else {
                ChatSplitView(
                    onNavigateToProfile = { userId ->
                        navController.navigate("profile/$userId/-1")
                    },
                    onNavigateBack = { navController.popBackStack() },
                    onNavigateToSettings = { navController.navigate(Routes.SETTINGS) }
                )
            }
        }

        composable(
            route = Routes.PROFILE_BY_USERNAME,
            arguments = listOf(navArgument("username") { type = NavType.StringType }),
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) { backStackEntry ->
            val username = getNavArgString(backStackEntry, "username") ?: ""
            ProfileScreen(
                userId = null,
                username = username,
                onNavigateBack = { navController.popBackStack() },
                onNavigateToPost = { profileUserId, postId ->
                    navController.navigate("home?tab=0&playUserId=$profileUserId&playPostId=$postId&startPostRef=")
                },
                onNavigateToEditProfile = { navController.navigate(Routes.EDIT_PROFILE) },
                onNavigateToSettings = { navController.navigate(Routes.SETTINGS) },
                onNavigateToMessages = { navController.navigate(Routes.MESSAGES) },
                onNavigateToChat = { receiverId, receiverName ->
                    navController.navigate("chat/$receiverId/$receiverName")
                },
                onNavigateToHome = { navController.navigateHome() },
                onNavigateToLogin = { authSheetController.open() },
                onLogout = { navController.navigateToOnboarding() },
                onBlocked = { navController.popBackStack() },
                onNavigateToFollowers = { id -> navController.navigate("profile/$id/followers") },
                onNavigateToFollowing = { id -> navController.navigate("profile/$id/following") },
                onNavigateToStories = { userId -> navController.navigate(Routes.stories(userId)) },
            )
        }

        composable(
            route = "profile/{userId}/followers",
            arguments = listOf(navArgument("userId") { type = NavType.StringType }),
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) { backStackEntry ->
            val profileUserId = getNavArgString(backStackEntry, "userId")?.toIntOrNull() ?: 0
            ProfileUserListScreen(
                userId = profileUserId,
                listType = "followers",
                onNavigateBack = { navController.popBackStack() },
                onNavigateToProfile = { id, _ -> navController.navigate("profile/$id/-1") }
            )
        }

        composable(
            route = "profile/{userId}/following",
            arguments = listOf(navArgument("userId") { type = NavType.StringType }),
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) { backStackEntry ->
            val profileUserId = getNavArgString(backStackEntry, "userId")?.toIntOrNull() ?: 0
            ProfileUserListScreen(
                userId = profileUserId,
                listType = "following",
                onNavigateBack = { navController.popBackStack() },
                onNavigateToProfile = { id, _ -> navController.navigate("profile/$id/-1") }
            )
        }

        composable(
            route = "profile/{userId}/{fromPostId}",
            arguments = listOf(
                navArgument("userId") { type = NavType.StringType },
                navArgument("fromPostId") { type = NavType.IntType; defaultValue = -1 }
            ),
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) { backStackEntry ->
            val userIdArg = getNavArgString(backStackEntry, "userId")?.toIntOrNull()
            val effectiveUserId = if (userIdArg == -1) null else userIdArg
            val fromPostId = getNavArgInt(backStackEntry, "fromPostId") ?: -1
            ProfileScreen(
                userId = effectiveUserId,
                fromPostId = if (fromPostId >= 0) fromPostId else null,
                onNavigateBack = { navController.popBackStack() },
                onNavigateToPost = { profileUserId, postId ->
                    navController.navigate("home?tab=0&playUserId=$profileUserId&playPostId=$postId&startPostRef=")
                },
                onNavigateToEditProfile = { navController.navigate(Routes.EDIT_PROFILE) },
                onNavigateToSettings = { navController.navigate(Routes.SETTINGS) },
                onNavigateToMessages = { navController.navigate(Routes.MESSAGES) },
                onNavigateToChat = { receiverId, receiverName ->
                    navController.navigate("chat/$receiverId/$receiverName")
                },
                onNavigateToHome = { navController.navigateHome() },
                onNavigateToLogin = { authSheetController.open() },
                onLogout = { navController.navigateToOnboarding() },
                onBlocked = { navController.popBackStack() },
                onNavigateToFollowers = { id -> navController.navigate("profile/$id/followers") },
                onNavigateToFollowing = { id -> navController.navigate("profile/$id/following") },
                onNavigateToStories = { userId -> navController.navigate(Routes.stories(userId)) },
            )
        }

        composable(
            route = Routes.PROFILE_FEED,
            arguments = listOf(
                navArgument("userId") { type = NavType.StringType },
                navArgument("startPostId") { type = NavType.StringType }
            ),
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) { backStackEntry ->
            val userId = getNavArgString(backStackEntry, "userId")?.toIntOrNull() ?: 0
            val startPostId = getNavArgString(backStackEntry, "startPostId")?.toIntOrNull() ?: 0
            ProfileFeedScreen(
                userId = userId,
                startPostId = startPostId,
                showBackButton = false,
                onNavigateBack = { navController.popBackStack() },
                onNavigateToProfile = { profileUserId ->
                    navController.navigate("profile/$profileUserId/-1")
                },
                onNavigateToMyProfile = { navController.navigateHome(tab = 3) },
                onNavigateToSettings = { navController.navigate(Routes.SETTINGS) },
                onFollowUser = { },
                onHashtagClick = { tag -> navController.navigate("hashtag/$tag") }
            )
        }

        composable(
            route = Routes.CHAT_DETAIL,
            arguments = listOf(
                navArgument("receiverId") { type = NavType.StringType },
                navArgument("receiverName") { type = NavType.StringType }
            ),
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) { backStackEntry ->
            val receiverId = getNavArgString(backStackEntry, "receiverId")?.toIntOrNull() ?: 0
            val receiverName = getNavArgString(backStackEntry, "receiverName") ?: ""
            if (isMobile()) {
                ChatDetailScreen(
                    receiverId = receiverId,
                    receiverName = receiverName,
                    onNavigateBack = { navController.popBackStack() },
                    onNavigateToProfile = { userId ->
                        navController.navigate("profile/$userId/-1")
                    },
                    onNavigateToSettings = { navController.navigate(Routes.SETTINGS) }
                )
            } else {
                ChatSplitView(
                    onNavigateToProfile = { userId ->
                        navController.navigate("profile/$userId/-1")
                    },
                    onNavigateBack = { navController.popBackStack() },
                    onNavigateToSettings = { navController.navigate(Routes.SETTINGS) },
                    initialChatId = receiverId,
                    initialChatName = receiverName
                )
            }
        }

        composable(
            route = Routes.CHAT_REQUESTS,
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) {
            ChatRequestsScreen(
                onNavigateBack = { navController.popBackStack() },
                onNavigateToChat = { receiverId, receiverName ->
                    navController.navigate("chat/$receiverId/$receiverName")
                },
                onNavigateToStories = { userId -> navController.navigate(Routes.stories(userId)) }
            )
        }

        composable(
            route = Routes.POST_DETAIL,
            arguments = listOf(navArgument("postRef") { type = NavType.StringType }),
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) { backStackEntry ->
            val postRef = getNavArgString(backStackEntry, "postRef") ?: ""
            PostDetailScreen(
                postRef = postRef,
                onNavigateBack = { navController.popBackStack() },
                onNavigateToProfile = { userId ->
                    navController.navigate("profile/$userId/-1")
                },
                onNavigateToHome = {
                    navController.navigate("home?tab=0&playUserId=-1&playPostId=-1&startPostRef=") {
                        popUpTo(Routes.HOME) { inclusive = true }
                        launchSingleTop = true
                    }
                },
                onNavigateToLogin = {
                    RedirectAfterLogin.route = DeepLinkRoute.Post(postRef)
                    authSheetController.open()
                },
                onNavigateToSettings = { navController.navigate(Routes.SETTINGS) },
                onNavigateToStories = { userId -> navController.navigate(Routes.stories(userId)) }
            )
        }

        composable(
            route = Routes.HASHTAG,
            arguments = listOf(
                navArgument("tag") { type = NavType.StringType }
            ),
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit
        ) { backStackEntry ->
            val tag = getNavArgString(backStackEntry, "tag") ?: ""
            val hashtagViewModel = remember(tag) {
                HashtagViewModel(
                    hashtagRepository = getKoin().get<HashtagRepository>(),
                    hashtag = tag
                )
            }
            HashtagScreen(
                hashtag = tag,
                viewModel = hashtagViewModel,
                onPostClick = { post, _ ->
                    navController.navigate("home?tab=0&playUserId=${post.author.id}&playPostId=${post.id}&startPostRef=")
                },
                onBack = { navController.popBackStack() }
            )
        }

        composable(
            route = Routes.STORIES,
            arguments = listOf(navArgument("userId") { type = NavType.IntType }),
            enterTransition = pushEnter,
            exitTransition = pushExit,
            popEnterTransition = pushPopEnter,
            popExitTransition = pushPopExit,
        ) { backStackEntry ->
            val userId = getNavArgInt(backStackEntry, "userId") ?: return@composable
            val postRepository: PostRepository = remember { getKoin().get() }
            StoryViewerScreen(
                userId = userId,
                postRepository = postRepository,
                onClose = { navController.popBackStack() },
            )
        }

    } // NavHost

    } // AdaptiveScaffold

    } // CompositionLocalProvider
}
